/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/box';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { captureException } from '@atlassian/error-handling';

import { fetchModalData } from '../../../../services/modal-data';
import ModalBackgroundDark from '../../assets/confirmationBackgroundV2.dark.png';
import ModalBackgroundLight from '../../assets/confirmationBackgroundV2.light.png';
import VisualDark from '../../assets/confirmationVisual.dark.svg';
import VisualLight from '../../assets/confirmationVisual.light.svg';
import TeamworkCollectionLogoDark from '../../assets/teamworkCollectionHeaderV2.dark.png';
import TeamworkCollectionLogoLight from '../../assets/teamworkCollectionHeaderV2.light.png';
import { ScreenLayout } from '../../components/screen-layout';
import { useModalContext } from '../../contexts/modal-context';
import { useIsDarkMode } from '../../hooks/useIsDarkMode';
import { hasRequestedTrial } from '../../utils/local-storage';

const styles = cssMap({
	content: {
		width: '100%',
		paddingTop: token('space.300'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		'@media (min-width: 64rem)': {
			paddingTop: token('space.600'),
			paddingLeft: token('space.600'),
			paddingRight: token('space.600'),
		},
	},
	cta: {
		marginTop: token('space.250'),
		flexGrow: 1,
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-end',
		alignItems: 'end',
	},
	modalContent: {
		display: 'flex',
		flexDirection: 'row',
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.large'),
	},
	leftColumn: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		paddingTop: token('space.500'),
		paddingLeft: token('space.500'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.500'),
	},
	rightColumn: {
		display: 'none',
		'@media (min-width: 64rem)': {
			flexShrink: 0,
			display: 'flex',
			paddingTop: token('space.400'),
			paddingLeft: token('space.400'),
			paddingRight: token('space.400'),
			paddingBottom: token('space.400'),
		},
	},
	heading: {
		marginTop: token('space.300'),
	},
	subheading: {
		marginTop: token('space.050'),
		font: token('font.body.small'),
	},
	newApps: {
		marginTop: token('space.300'),
	},
	newApp: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		marginTop: token('space.100'),
	},
	newAppFirst: {
		marginTop: 0,
	},
	newAppText: {
		marginLeft: token('space.150'),
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	existingAppsHeader: {
		marginTop: token('space.300'),
		font: token('font.body.small'),
	},
	existingApps: {
		display: 'flex',
		flexDirection: 'row',
		gap: token('space.200'),
	},
	existingAppText: {
		marginLeft: token('space.075'),
	},
	legalText: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '56px',
		textAlign: 'center',
	},
	secondLine: {
		marginTop: token('space.100'),
	},
});

type LoadingScreenProps = {
	setApprovalUrl: (approvalUrl: string | undefined) => void;
};

export const LoadingScreen: ({ setApprovalUrl }: LoadingScreenProps) => JSX.Element = ({
	setApprovalUrl,
}: LoadingScreenProps): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isDarkMode = useIsDarkMode();
	const { cloudId, sourceComponent, sourceContext, setCurrentScreen, setModalState } =
		useModalContext();

	useEffect(() => {
		const loadModalData = async () => {
			try {
				const data = await fetchModalData(cloudId, sourceComponent, sourceContext);

				if (data.modalData.activationCapability !== 'can-activate') {
					const [hasRequested, requestedTrialData] = hasRequestedTrial(cloudId);
					if (hasRequested && requestedTrialData) {
						setModalState(data.modalData);
						setApprovalUrl(requestedTrialData.link);
						setCurrentScreen('requested-trial-screen');
						return;
					}
					setModalState(data.modalData);
					setCurrentScreen('confirmation');
					return;
				}

				if (!data.allowed && !data.inProgress && !data.modalData?.status) {
					setCurrentScreen('error');
					return;
				}

				setModalState(data.modalData);
				if (data.inProgress || data.modalData?.status) {
					setCurrentScreen('provisioning');
				} else {
					setCurrentScreen('confirmation');
				}
			} catch (error) {
				setCurrentScreen('error');
				captureException('Failed to fetch TWC XFlow modal data', error as Error, {
					cloudId,
					packageName: '@atlassian/twc-xflow-modal',
					teamName: 'starburst',
				});
			}
		};

		loadModalData();
	}, [cloudId, setCurrentScreen, setModalState, setApprovalUrl, sourceComponent, sourceContext]);

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcXflowModalLoadingScreen',
			attributes: {
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');
	}, [createAnalyticsEvent, sourceComponent, sourceContext]);

	useEffect(() => {
		const preloadImages = isDarkMode
			? [TeamworkCollectionLogoDark, ModalBackgroundDark, VisualDark]
			: [TeamworkCollectionLogoLight, ModalBackgroundLight, VisualLight];
		preloadImages.map((src) => {
			new Image().src = src;
		});
	}, [isDarkMode]);

	return (
		<ScreenLayout testId="loading-screen">
			<Box xcss={styles.content}>
				<Box xcss={styles.modalContent}>
					<Box xcss={styles.leftColumn}>
						<Box>
							<Skeleton width="134px" height="32px" />
						</Box>

						<Box xcss={styles.heading}>
							<Skeleton width="100%" height="20px" />
							<Box xcss={styles.secondLine}>
								<Skeleton width="100%" height="20px" />
							</Box>
						</Box>

						<Box xcss={styles.subheading}>
							<Skeleton width="200px" height="16px" />
						</Box>

						<Box xcss={styles.newApps}>
							{[1, 2].map((newApp, index) => {
								return (
									<Box key={newApp} xcss={cx(styles.newApp, index === 0 && styles.newAppFirst)}>
										<Skeleton width="32px" height="32px" />
										<Box xcss={styles.newAppText}>
											<Skeleton width="60px" height="16px" />
											<Box xcss={styles.secondLine}>
												<Skeleton width="200px" height="12px" />
											</Box>
										</Box>
									</Box>
								);
							})}
						</Box>

						<Box xcss={styles.existingAppsHeader}>
							<Skeleton width="200px" height="16px" />
						</Box>

						<Box xcss={styles.existingApps}>
							{[1, 2].map((existingApp) => {
								return (
									<Box key={existingApp} xcss={styles.newApp}>
										<Skeleton width="32px" height="20px" />
										<Box xcss={styles.existingAppText}>
											<Skeleton width="60px" height="20px" />
										</Box>
									</Box>
								);
							})}
						</Box>

						<Box xcss={styles.cta}>
							<Skeleton width="132px" height="32px" />
						</Box>
					</Box>
					<Box xcss={styles.rightColumn}>
						<Skeleton width="345px" height="400px" color={token('elevation.surface.sunken')} />
					</Box>
				</Box>

				{/* Legal Text */}
				<Box xcss={styles.legalText}>
					<Skeleton width="100%" height="20px" />
				</Box>
			</Box>
		</ScreenLayout>
	);
};
